import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Fade from "@mui/material/Fade";
import Zoom from "@mui/material/Zoom";
import LinearProgress from "@mui/material/LinearProgress";

export interface LoadingSpinnerProps {
	text?: string;
	subtext?: string;
	progress?: number;
	fullScreen?: boolean;
	size?: number;
	color?: "inherit" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
}

export const LoadingSpinner = ({
	text = "ExpoWiz",
	subtext: initialSubtext,
	progress: initialProgress,
	fullScreen = false,
	size = 24,
	color = "primary",
}: LoadingSpinnerProps) => {
	const [subtext, setSubtext] = useState(initialSubtext);
	const [progress, setProgress] = useState(initialProgress);

	// Sync with global loading events
	useEffect(() => {
		if (!fullScreen) return;

		const handleProgress = (e: any) => {
			const { message, progress: p } = e.detail;
			console.log('[LoadingSpinner] Unifying state from event:', message, p);
			setSubtext(message);
			setProgress(p);
		};

		window.addEventListener('loading-progress', handleProgress);
		
		// If there's an existing state in the HTML splash, it might be stale.
		// However, we can't easily read it back. We rely on the fact that 
		// main.tsx or pwa-init.ts will call setLoadingStatus again soon.

		return () => window.removeEventListener('loading-progress', handleProgress);
	}, [fullScreen]);

	// Update local state if props change (for normal usage)
	useEffect(() => {
		if (initialSubtext !== undefined) setSubtext(initialSubtext);
		if (initialProgress !== undefined) setProgress(initialProgress);
	}, [initialSubtext, initialProgress]);

	const content = (
		<Stack spacing={4} alignItems="center" justifyContent="center">
			{/* Zoom is disabled for fullScreen to prevent jarring entry from HTML splash */}
			<Zoom in timeout={fullScreen ? 0 : 1000}>
				<Box
					sx={{
						position: 'relative',
						filter: fullScreen ? 'drop-shadow(0 0 20px rgba(255,255,255,0.3))' : 'none'
					}}
				>
					<CircularProgress size={fullScreen ? 72 : size} color={color} variant={progress !== undefined ? "determinate" : "indeterminate"} value={progress} />
				</Box>
			</Zoom>
			<Fade in timeout={fullScreen ? 0 : 1000} style={{ transitionDelay: fullScreen ? '0ms' : '300ms' }}>
				<Stack spacing={1.5} alignItems="center">
					<Typography
						variant={fullScreen ? "h3" : "h6"}
						fontWeight={900}
						sx={{
							letterSpacing: '-2px',
							textShadow: fullScreen ? '0 4px 12px rgba(0,0,0,0.2)' : 'none',
							color: fullScreen ? "common.white" : "text.primary"
						}}
					>
						{text}
					</Typography>
					{(subtext || progress !== undefined) && (
						<Stack spacing={1} alignItems="center" sx={{ width: '100%', minWidth: 200 }}>
							{subtext && (
								<Typography
									variant="body2"
									sx={{
										opacity: 0.9,
										fontWeight: 500,
										letterSpacing: '1px',
										textTransform: 'uppercase',
										fontSize: '0.75rem',
										color: fullScreen ? "common.white" : "text.secondary"
									}}
								>
									{subtext}
								</Typography>
							)}
							{progress !== undefined && (
								<Box sx={{ width: '100%' }}>
									<LinearProgress
										variant="determinate"
										value={progress}
										sx={{
											height: 4,
											borderRadius: 2,
											bgcolor: fullScreen ? "rgba(255, 255, 255, 0.2)" : "action.hover",
											"& .MuiLinearProgress-bar": {
												bgcolor: fullScreen ? "common.white" : "primary.main",
											},
										}}
									/>
								</Box>
							)}
						</Stack>
					)}
				</Stack>
			</Fade>
		</Stack>
	);

	if (fullScreen) {
		return (
			<Fade in timeout={200}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						minHeight: "100dvh",
						background: (theme) => theme.palette.mode === 'dark'
							? "radial-gradient(circle at center, #001a35 0%, #020617 100%)"
							: "linear-gradient(135deg, #0284c7 0%, #0ea5e9 100%)",
						color: "white",
						zIndex: 9999,
						position: "fixed",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
					}}
				>
					{content}
				</Box>
			</Fade>
		);
	}

	return (
		<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
			{content}
		</Box>
	);
}
